import { analyticsLabelZh } from '../../scripts/analytics-labels.mjs';

export const CORE_FUNNEL_EVENTS = [
  'chart_form_started',
  'chart_generated',
  'intent_preview_viewed',
  'activation_completed',
  'offer_viewed',
  'offer_selected',
  'begin_checkout',
  'purchase',
  'pdf_downloaded',
] as const;

export type AnalyticsPageType =
  | 'home'
  | 'calculator'
  | 'guide_index'
  | 'guide'
  | 'report_sales'
  | 'report_demo'
  | 'sample_report'
  | 'methodology'
  | 'tarot_tool'
  | 'chinese_research'
  | 'local_research'
  | 'other';

export type AnalyticsChartSystem = 'ziwei' | 'bazi' | 'tarot' | 'research' | 'other';

type AnalyticsPayload = Record<string, unknown>;
type TrackOptions = { dedupeKey?: string; dedupeScope?: 'page' | 'session' };
type AnalyticsWindow = Window & {
  gtag?: (...args: unknown[]) => void;
  __zihengAnalytics?: {
    events: Array<{ event: string; payload: AnalyticsPayload }>;
    staticClicksInstalled?: boolean;
  };
};

const PAGE_DEDUPE_KEYS = new Set<string>();
const SESSION_DEDUPE_PREFIX = 'ziheng:analytics:event:';
const LANDING_PATH_KEY = 'ziheng:analytics:landing-path';
const LANDING_REFERRER_KEY = 'ziheng:analytics:landing-referrer';
const TRACKING_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

export function analyticsPageType(pathname: string): AnalyticsPageType {
  if (/^\/(?:en|zh|zh-hant)\/bazi\/(?:index\.html)?$/.test(pathname)) return 'home';
  if (/^\/(?:en|zh|zh-hant)\/(?:bazi\/)?app\.html$/.test(pathname)) return 'calculator';
  if (/^\/en\/tarot\/(?:(?:index|love-reading)\.html)?$/.test(pathname)) return 'tarot_tool';
  if (/^\/en\/(?:tarot\/(?:card-meanings|major-arcana)|astrology\/guides)\/(?:index\.html)?$/.test(pathname)) return 'guide_index';
  if (/^\/en\/(?:tarot|astrology\/guides)\//.test(pathname)) return 'guide';
  if (pathname === '/en/' || pathname === '/en/index.html') return 'home';
  if (pathname === '/zh/' || pathname === '/zh/index.html' || pathname === '/zh-hant/' || pathname === '/zh-hant/index.html') return 'home';
  if (pathname === '/en/app.html' || pathname === '/en/bazi/app.html' || pathname === '/zh-hant/bazi/app.html') return 'calculator';
  if (/^\/(?:en|zh-hant)\/bazi\/guides\/(?:index\.html)?$/.test(pathname)) return 'guide_index';
  if (/^\/(?:en|zh-hant)\/bazi\/guides\//.test(pathname)) return 'guide';
  if (/^\/(?:en|zh-hant)\/bazi\/reports\//.test(pathname)) return 'report_sales';
  if (/^\/(?:en|zh-hant)\/bazi\/demo\//.test(pathname)) return 'report_demo';
  if (pathname === '/zh-hant/guides/' || pathname === '/zh-hant/guides/index.html') return 'guide_index';
  if (pathname.startsWith('/zh-hant/guides/')) return 'guide';
  if (pathname.startsWith('/zh-hant/reports/')) return 'report_sales';
  if (pathname.startsWith('/zh-hant/demo/')) return 'report_demo';
  if (pathname === '/zh/guides/' || pathname === '/zh/guides/index.html') return 'guide_index';
  if (pathname.startsWith('/zh/guides/')) return 'guide';
  if (pathname.startsWith('/zh/reports/')) return 'report_sales';
  if (pathname === '/en/guides/' || pathname === '/en/guides/index.html') return 'guide_index';
  if (pathname.startsWith('/en/guides/')) return 'guide';
  if (pathname.startsWith('/en/reports/')) return 'report_sales';
  if (pathname.startsWith('/en/demo/')) return 'report_demo';
  if (pathname === '/en/sample-report.html') return 'sample_report';
  if (pathname.startsWith('/en/methodology/') || pathname.startsWith('/zh-hant/methodology/')) return 'methodology';
  if (pathname.startsWith('/zh/')) return 'chinese_research';
  if (pathname === '/') return 'local_research';
  return 'other';
}

export function analyticsChartSystem(pathname: string): AnalyticsChartSystem {
  if (/^\/en\/tarot(?:\/|$)/.test(pathname)) return 'tarot';
  if (/^\/(?:en|zh|zh-hant)\/bazi(?:\/|$)/.test(pathname)) return 'bazi';
  if (/^\/(?:en|zh|zh-hant)(?:\/|$)/.test(pathname)) return 'ziwei';
  if (pathname === '/' || pathname.startsWith('/zh/')) return 'research';
  return 'other';
}

export function analyticsFunnelStage(event: string) {
  if (event === 'activation_completed') return '03_activation_candidate';
  if (['first_insight_engaged', 'insight_evidence_opened', 'insight_resonance_selected', 'reading_action_selected', 'activation_nudge_clicked'].includes(event)) return '03_reading_engagement';
  if (['calculator_cta_clicked', 'guide_cta_clicked', 'report_sales_cta_clicked', 'report_demo_cta_clicked'].includes(event)) return '01_to_calculator';
  if (['chart_generated', 'bazi_chart_generated'].includes(event)) return '02_chart';
  if (event === 'chart_form_started') return '01_form';
  if (event === 'chart_entry_submitted') return '01_form_handoff';
  if (['intent_preview_viewed', 'personalized_preview_viewed', 'bazi_preview_viewed', 'crosscheck_preview_viewed'].includes(event)) return '03_preview';
  if (['offer_viewed', 'report_offer_viewed', 'bazi_offer_viewed', 'offer_selected', 'conversion_cta_clicked'].includes(event)) return '04_offer';
  if (['begin_checkout', 'checkout_modal_opened'].includes(event)) return '05_checkout';
  if (['purchase', 'snapshot_purchased', 'upgrade_purchased'].includes(event)) return '06_purchase';
  if (['pdf_downloaded', 'bazi_pdf_downloaded'].includes(event)) return '07_fulfillment';
  return undefined;
}

export function safeAnalyticsValue(value: string | null | undefined, fallback = 'none', maxLength = 100) {
  if (value && /[\w.+-]+@[\w.-]+\.[a-z]{2,}|(?:sk_|pk_|whsec_)/i.test(value)) return fallback;
  const normalized = (value || '').replace(/[^a-z0-9._\-/:]/gi, '').slice(0, maxLength);
  return normalized || fallback;
}

export function analyticsQueryContext(search: string) {
  const params = new URLSearchParams(search);
  const focus = params.get('focus');
  return {
    entry_focus: ['self', 'career', 'wealth', 'annual', 'relationship'].includes(focus || '') ? focus : 'career',
    entry_slug: safeAnalyticsValue(params.get('entry'), 'direct', 80),
    ...Object.fromEntries(TRACKING_KEYS
      .map((key) => [key, safeAnalyticsValue(params.get(key), '', 100)] as const)
      .filter(([, value]) => Boolean(value))),
  };
}

export function analyticsLandingPath(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  const safeParams = new URLSearchParams();
  const focus = params.get('focus');
  if (['self', 'career', 'wealth', 'annual', 'relationship'].includes(focus || '')) safeParams.set('focus', focus!);
  const entry = safeAnalyticsValue(params.get('entry'), '', 80);
  if (entry) safeParams.set('entry', entry);
  for (const key of TRACKING_KEYS) {
    const value = safeAnalyticsValue(params.get(key), '', 100);
    if (value) safeParams.set(key, value);
  }
  const query = safeParams.toString();
  return `${pathname}${query ? `?${query}` : ''}`.slice(0, 180);
}

function safeSessionGet(key: string) {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSessionSet(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Analytics must never block the product when storage is unavailable.
  }
}

function landingContext() {
  const currentPath = analyticsLandingPath(window.location.pathname, window.location.search);
  let landingPath = safeSessionGet(LANDING_PATH_KEY);
  if (!landingPath) {
    landingPath = currentPath;
    safeSessionSet(LANDING_PATH_KEY, landingPath);
  }
  let landingReferrer = safeSessionGet(LANDING_REFERRER_KEY);
  if (landingReferrer === null) {
    try {
      landingReferrer = document.referrer ? new URL(document.referrer).hostname : 'direct';
    } catch {
      landingReferrer = 'unknown';
    }
    safeSessionSet(LANDING_REFERRER_KEY, landingReferrer);
  }
  return {
    landing_path: landingPath.slice(0, 180),
    landing_referrer: safeAnalyticsValue(landingReferrer, 'direct', 100),
  };
}

function commonContext() {
  const pageType = analyticsPageType(window.location.pathname);
  return {
    event_version: 1,
    page_type: pageType,
    entry_surface: pageType === 'calculator' ? 'calculator' : 'page',
    chart_system: analyticsChartSystem(window.location.pathname),
    page_language: document.documentElement.lang || 'unknown',
    ...analyticsQueryContext(window.location.search),
    ...landingContext(),
  };
}

function shouldSkipEvent(options: TrackOptions) {
  if (!options.dedupeKey) return false;
  const key = safeAnalyticsValue(options.dedupeKey, 'event', 180);
  if (options.dedupeScope === 'session') {
    const storageKey = `${SESSION_DEDUPE_PREFIX}${key}`;
    if (safeSessionGet(storageKey)) return true;
    safeSessionSet(storageKey, '1');
    return false;
  }
  if (PAGE_DEDUPE_KEYS.has(key)) return true;
  PAGE_DEDUPE_KEYS.add(key);
  return false;
}

export function cleanAnalyticsPayload(payload: AnalyticsPayload): AnalyticsPayload {
  // Defense in depth: reject raw user/chart data even if a future caller adds it.
  const forbidden = /birth|email|question|longitude|latitude|timezoneoffset|fingerprint|secret|client.?id|session.?id|time_slot|traditional_gender|current_cycle|day_master/i;
  const clean: AnalyticsPayload = {};
  for (const [key, value] of Object.entries(payload)) {
    if (forbidden.test(key) && key !== 'has_birthplace') continue;
    if (key === 'items' && Array.isArray(value)) {
      clean.items = value.slice(0, 10).map((item) => cleanAnalyticsPayload(Object.fromEntries(
        Object.entries(item || {}).filter(([name]) => ['item_id', 'item_name', 'item_category', 'price', 'quantity', 'currency'].includes(name)),
      )));
    } else if (typeof value === 'number' && Number.isFinite(value) || typeof value === 'boolean') {
      clean[key] = value;
    } else if (typeof value === 'string' && value && !/[\w.+-]+@[\w.-]+\.[a-z]{2,}|(?:sk_|pk_|whsec_)/i.test(value)) {
      clean[key] = value.slice(0, key === 'landing_path' || key === 'destination' ? 180 : 100);
    }
  }
  return clean;
}

export function trackAnalytics(event: string, payload: AnalyticsPayload = {}, options: TrackOptions = {}) {
  if (typeof window === 'undefined' || shouldSkipEvent(options)) return;
  const analyticsWindow = window as AnalyticsWindow;
  const completePayload = cleanAnalyticsPayload({ ...commonContext(), funnel_stage: analyticsFunnelStage(event), ...payload, event_label_zh: analyticsLabelZh(event) });
  analyticsWindow.__zihengAnalytics ||= { events: [] };
  analyticsWindow.__zihengAnalytics.events.push({ event, payload: completePayload });
  analyticsWindow.__zihengAnalytics.events = analyticsWindow.__zihengAnalytics.events.slice(-100);
  analyticsWindow.gtag?.('event', event, completePayload);
  window.dispatchEvent(new CustomEvent('ziheng:analytics', { detail: { event, payload: completePayload } }));
  if (new URLSearchParams(window.location.search).get('analytics_debug') === '1') {
    console.info('[Ziheng analytics]', event, completePayload);
  }
}

function ctaPlacement(anchor: HTMLAnchorElement) {
  if (anchor.closest('.mvp-nav')) return 'navigation';
  if (anchor.closest('.mvp-hero')) return 'hero';
  if (anchor.closest('.mvp-guide-action, .ed-guide-action, .adj-guide-action')) return 'hero';
  if (anchor.closest('.mvp-pricing')) return 'pricing';
  if (anchor.closest('.mvp-article-cta')) return 'article_cta';
  if (anchor.closest('.mvp-inline-conversion, .ed-inline-conversion, .adj-conversion, .tarot-conversion')) return 'contextual_conversion';
  if (anchor.closest('footer')) return 'footer';
  if (anchor.closest('.ed-aside')) return 'article_cta';
  return 'inline';
}

export function installStaticAnalyticsClicks() {
  if (typeof window === 'undefined') return;
  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.__zihengAnalytics ||= { events: [] };
  if (analyticsWindow.__zihengAnalytics.staticClicksInstalled) return;
  analyticsWindow.__zihengAnalytics.staticClicksInstalled = true;

  document.addEventListener('click', (event) => {
    const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null;
    if (!anchor) return;
    let destination: URL;
    try {
      destination = new URL(anchor.href, window.location.origin);
    } catch {
      return;
    }
    if (destination.origin !== window.location.origin) return;
    const shared = {
      source_page: window.location.pathname,
      destination: analyticsLandingPath(destination.pathname, destination.search),
      cta_placement: ctaPlacement(anchor),
    };

    if (/^\/(?:en|zh|zh-hant)\/(?:bazi\/)?demo\//.test(destination.pathname)) {
      trackAnalytics('report_demo_opened', shared);
      return;
    }
    const sourceIsGuide = /^\/(?:en|zh|zh-hant)\/(?:bazi\/)?guides\//.test(window.location.pathname)
      || /^\/en\/astrology\/guides\//.test(window.location.pathname)
      || /^\/en\/tarot\//.test(window.location.pathname);
    const destinationIsGuideAction = /\/(?:bazi\/)?app\.html$/.test(destination.pathname)
      || destination.pathname === '/en/tarot/';
    if (sourceIsGuide && destinationIsGuideAction) {
      const target = analyticsQueryContext(destination.search);
      trackAnalytics('guide_cta_clicked', {
        ...shared,
        guide_slug: safeAnalyticsValue(window.location.pathname.split('/').filter(Boolean).pop()?.replace(/\.html$/, ''), 'guides', 80),
        destination_focus: target.entry_focus,
        destination_entry: target.entry_slug,
      });
      return;
    }
    if (window.location.pathname === '/en/sample-report.html' && destination.pathname === '/en/app.html') {
      trackAnalytics('report_sample_cta_clicked', shared);
      return;
    }
    if (/^\/(?:en|zh|zh-hant)\/(?:bazi\/)?reports\//.test(window.location.pathname) && /\/(?:bazi\/)?app\.html$/.test(destination.pathname)) {
      trackAnalytics('report_sales_cta_clicked', shared);
      return;
    }
    if (/^\/(?:en|zh|zh-hant)\/(?:bazi\/)?demo\//.test(window.location.pathname) && /\/(?:bazi\/)?app\.html$/.test(destination.pathname)) {
      trackAnalytics('report_demo_cta_clicked', shared);
      return;
    }
    if (/\/(?:bazi\/)?app\.html$/.test(destination.pathname)) trackAnalytics('calculator_cta_clicked', shared);
  });
}
